import {WriteButton, WriteIconButton} from '../../common/buttons/WriteActions';
import {useAccountDeletion} from '../../../models/AccountDeletion';
import {validateName} from '../../../common/nameValidation';
import React, {useEffect, useMemo, useState} from 'react';
import {Alert, Autocomplete, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, InputAdornment, MenuItem, Snackbar, Stack, TextField, Typography} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import SendIcon from '@mui/icons-material/Send';
import {ItemDto} from '../../../models/Items/IItem';
import {IRecipe} from '../../../models/Recipe/IRecipe';
import {IRecipePart} from '../../../models/Recipe/IRecipePart';
import {IRecipePartItem} from '../../../models/Recipe/IRecipePartItem';
import {GuidLightDto} from '../../../models/common/IGuidLight';
import {DTO_STATE, UNIT_OF_WEIGHT} from '@mybake/ui/common/dictionary';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {ProductTypeSelect} from '../productTypes/ProductTypeSelect';
import {isDuplicateRecipeName, makeRecipe, makeRecipeItem, makeRecipePart, trimOrEmpty, itemQuantity, recipeComparisonSnapshot, recipePartComparisonSnapshot, recipePartGuidForForm, recipePartGuidRequest, updatePrimaryProportionQuantity} from './recipeFormHelpers';
import {normalizeApiError, getApiErrorKind, getApiErrorMessage, getApiFieldErrors, getApiTraceId} from '../../../common/apiErrors';
import TraceIdFeedback from '../../feedback/TraceIdFeedback';
import {LocalizedDecimalField} from '../../common/inputs/LocalizedDecimalField';
import {selectedIngredientUnit} from './ingredientSelection';
import {recipeAPI} from '../../../services/RecipeServices';
import {formatDecimal, getIngredientAmount} from './ingredientAmount';
import useDebounce from '../../../hooks/useDebounce';

type Props = {
    initialData?: IRecipe;
    items?: ItemDto[];
    fillings?: GuidLightDto[];
    productTypes?: GuidLightDto[];
    onSubmit: (value: IRecipe) => Promise<void>;
    submitLabel: string;
};

type ErrorMap = Record<string, string>;
type TouchedMap = Record<string, boolean>;

const recipeItemRowSx = {
    display: 'grid',
    gridTemplateColumns: {xs: 'minmax(0, 1fr) 110px 44px', sm: 'minmax(0, 1fr) 140px 44px'},
    gridTemplateAreas: '"name quantity action"',
    gap: 1,
    alignItems: 'start',
    width: '100%',
    minWidth: 0,
};

const recipeItemActionSx = {
    gridArea: 'action',
    width: 44,
    height: 44,
    minWidth: 44,
    minHeight: 44,
};

const animatedHelperSx = (visible: boolean) => ({
    maxHeight: visible ? 64 : 0,
    opacity: visible ? 1 : 0,
    mt: visible ? 0.5 : 0,
    overflow: 'hidden',
    transition: 'max-height 180ms ease, opacity 180ms ease, margin-top 180ms ease',
});

const recipeForForm = (source?: IRecipe) => {
    const value = source
        ? {
            ...source,
            parts: source.parts?.map(part => ({
                ...part,
                items: (part.items ?? []).map(item => ({
                    ...item,
                    proportions: item.proportions?.length
                        ? item.proportions
                        : [{...makeRecipeItem(part.id).proportions[0], recipePartItemId: item.id}],
                })),
            })),
        }
        : makeRecipe();
    return value;
};

export default function RecipeForm({initialData, items = [], fillings = [], productTypes = [], onSubmit, submitLabel}: Props) {
    const {isReadOnly} = useAccountDeletion();
    const {t} = useAppTranslation();
    const [form, setForm] = useState<IRecipe>(() => recipeForForm(initialData));
    const debouncedForm = useDebounce(form, 400);
    const [submitted, setSubmitted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [touched, setTouched] = useState<TouchedMap>({});
    const [serverErrors, setServerErrors] = useState<ErrorMap>({});
    const [submitError, setSubmitError] = useState('');
    const [serverToast, setServerToast] = useState('');
    const [traceId, setTraceId] = useState('');
    const [libraryOpen, setLibraryOpen] = useState(false);
    const [librarySearch, setLibrarySearch] = useState('');
    const [libraryCategory, setLibraryCategory] = useState('all');
    const [sharedWarningOpen, setSharedWarningOpen] = useState(false);
    const [partToDetach, setPartToDetach] = useState<IRecipePart | null>(null);
    const [partToDeleteFromLibrary, setPartToDeleteFromLibrary] = useState<IRecipePart | null>(null);
    const [libraryDeleteConfirmation, setLibraryDeleteConfirmation] = useState('');
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [categoryTargetPartId, setCategoryTargetPartId] = useState<number | string | null>(null);
    const [savingLibraryCategoryId, setSavingLibraryCategoryId] = useState<number | string | null>(null);
    const debouncedLibrarySearch = useDebounce(librarySearch, 300);
    const {data: libraryResponse = [], isLoading: libraryLoading} = recipeAPI.useGetRecipePartsV2Query();
    const libraryParts = useMemo(() => libraryResponse
        .map(part => recipePartGuidForForm(part))
        .filter(part => libraryCategory === 'all'
            || (libraryCategory === 'uncategorized' ? !part.categoryId : part.categoryId === libraryCategory))
        .filter(part => !debouncedLibrarySearch.trim()
            || (part.partName ?? '').toLocaleLowerCase().includes(debouncedLibrarySearch.trim().toLocaleLowerCase()))
        .sort((left, right) => (right.usedByRecipesCount ?? 0) - (left.usedByRecipesCount ?? 0)), [debouncedLibrarySearch, libraryCategory, libraryResponse]);
    const {data: partCategories = [], isLoading: categoriesLoading} = recipeAPI.useGetRecipePartCategoriesQuery();
    const {data: recipeNames, isFetching: recipesLoading, error: recipeNamesError} = recipeAPI.useGetAvailableRecipeNamesQuery(undefined, {refetchOnMountOrArgChange: true});
    const namesUnavailable = recipesLoading || !recipeNames || !!recipeNamesError;
    const [deleteRecipePart, {isLoading: deletingLibraryPart}] = recipeAPI.useDeleteRecipePartV2Mutation();
    const [createRecipePartCategory, {isLoading: creatingCategory}] = recipeAPI.useCreateRecipePartCategoryMutation();
    const [updateRecipePart] = recipeAPI.useUpdateRecipePartV2Mutation();

    useEffect(() => {
        setForm(recipeForForm(initialData));
        setSubmitted(false);
        setTouched({});
        setServerErrors({});
        setSubmitError('');
    }, [initialData]);

    const initialSnapshot = useMemo(() => recipeComparisonSnapshot(initialData), [initialData]);
    const currentSnapshot = useMemo(() => recipeComparisonSnapshot(form), [form]);
    const isDirty = JSON.stringify(initialSnapshot) !== JSON.stringify(currentSnapshot);
    const selectedFillings = fillings.filter(filling => (form.recipeFillings ?? []).some(relation => String(relation.fillingId) === filling.id));
    const updateFillings = (values: GuidLightDto[]) => update({
        recipeFillings: values.map(filling => ({
            recipeId: form.id,
            fillingId: filling.id,
            recipeName: form.recipeName,
            fillingName: filling.name,
            state: DTO_STATE.DTO_UNCHANGED,
        })),
    });

    const errors = useMemo<ErrorMap>(() => {
        const next: ErrorMap = {};
        if (!trimOrEmpty(form.recipeName)) next.recipeName = t('recipes:validation_required');
        else if (validateName(form.recipeName)) next.recipeName = t('common:name_min_length');
        else if (isDuplicateRecipeName(form.recipeName, recipeNames ?? [], initialData?.recipeName)) next.recipeName = t('recipes:validation_recipe_name_exists');

        (form.parts ?? []).forEach((part, partIndex) => {
            if (part.state === DTO_STATE.DTO_DELETED) return;
            if (!trimOrEmpty(part.partName)) next[`part.${partIndex}.name`] = t('recipes:validation_required');
            else if (validateName(part.partName)) next[`part.${partIndex}.name`] = t('common:name_min_length');
            const seen = new Set<string>();

            (part.items ?? []).forEach((item, itemIndex) => {
                if (item.state === DTO_STATE.DTO_DELETED) return;
                const itemNameField = `part.${partIndex}.item.${itemIndex}.name`;
                const quantityField = `part.${partIndex}.item.${itemIndex}.quantity`;
                const hasItem = item.itemId !== -1 && String(item.itemId).length > 0;
                if (!hasItem || !trimOrEmpty(item.itemName)) next[itemNameField] = t('recipes:validation_select_ingredient');
                if (hasItem && seen.has(String(item.itemId))) next[itemNameField] = t('recipes:validation_duplicate_ingredient');
                if (hasItem) seen.add(String(item.itemId));

                const quantity = item.proportions?.[0]?.quantity;
                if (quantity === undefined || quantity === null || String(quantity).trim() === '' || !Number.isFinite(Number(quantity)) || Number(quantity) <= 0) {
                    next[quantityField] = t('recipes:validation_positive_number');
                }
            });
        });

        return next;
    }, [form, initialData?.recipeName, recipeNames, t]);

    const hasValidationErrors = Object.keys(errors).length > 0;
    const markTouched = (field: string) => setTouched(previous => ({...previous, [field]: true}));
    const fieldError = (field: string) => {
        if (serverErrors[field]) return serverErrors[field];
        const partNameMatch = /^part\.(\d+)\.name$/.exec(field);
        const namePending = field === 'recipeName'
            ? form.recipeName !== debouncedForm.recipeName
            : partNameMatch ? form.parts?.[Number(partNameMatch[1])]?.partName !== debouncedForm.parts?.[Number(partNameMatch[1])]?.partName : false;
        return errors[field] && (submitted || (touched[field] && !namePending)) ? errors[field] : '';
    };
    const fieldHasError = (field: string) => Boolean(fieldError(field));
    const hasVisibleValidationErrors = Object.keys(errors).some(fieldHasError);

    const update = (patch: Partial<IRecipe>) => setForm(previous => ({
        ...previous,
        ...patch,
        state: initialData ? DTO_STATE.DTO_MODIFIED : previous.state,
    }));
    const updatePart = (partId: number | string, patch: Partial<IRecipePart>) => setForm(previous => ({
        ...previous,
        parts: previous.parts?.map(part => part.id === partId
            ? {
                ...part,
                ...patch,
                state: initialData && part.state !== DTO_STATE.DTO_NEW
                    ? DTO_STATE.DTO_MODIFIED
                    : part.state,
            }
            : part),
        state: initialData ? DTO_STATE.DTO_MODIFIED : previous.state,
    }));
    const updateItem = (partId: number | string, itemId: number | string, patch: Partial<IRecipePartItem>) => setForm(previous => ({
        ...previous,
        parts: previous.parts?.map(part => part.id === partId
            ? {
                ...part,
                state: initialData && part.state !== DTO_STATE.DTO_NEW
                    ? DTO_STATE.DTO_MODIFIED
                    : part.state,
                items: part.items?.map(item => item.id === itemId
                    ? {
                        ...item,
                        ...patch,
                        state: initialData && item.state !== DTO_STATE.DTO_NEW
                            ? DTO_STATE.DTO_MODIFIED
                            : item.state,
                    }
                    : item),
            }
            : part),
        state: initialData ? DTO_STATE.DTO_MODIFIED : previous.state,
    }));

    const saveForm = async () => {
        if (saving || namesUnavailable || hasValidationErrors) return;
        setSaving(true);
        setServerErrors({});
        setSubmitError('');
        try {
            await onSubmit({...form, recipeName: trimOrEmpty(form.recipeName), recipeDescription: trimOrEmpty(form.recipeDescription)});
        } catch (error) {
            const apiFields = getApiFieldErrors(error, t);
            const mapped = Object.fromEntries(Object.entries(apiFields).map(([field, message]) => [field === 'name' || field === 'recipe' ? 'recipeName' : field, message]));
            if (normalizeApiError(error).code === 'recipe.name.conflict') mapped.recipeName = t('recipes:validation_recipe_name_exists');
            setServerErrors(mapped);
            setTraceId(getApiTraceId(error));
            if (getApiErrorKind(error) === 'server') setServerToast(getApiErrorMessage(error, t));
            else if (!Object.keys(mapped).some(field => field === 'recipeName' || field.startsWith('part.'))) setSubmitError(getApiErrorMessage(error, t));
        } finally {
            setSaving(false);
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setSubmitted(true);
        if (saving || namesUnavailable || hasValidationErrors) return;
        const changesSharedPart = (form.parts ?? []).some(part => {
            if (part.state !== DTO_STATE.DTO_MODIFIED || (part.usedByRecipesCount ?? 0) <= 1) return false;
            const current = JSON.stringify(recipePartComparisonSnapshot(part));
            if (part.clientOrigin === 'library') return current !== part.clientOriginalSnapshot;
            const original = initialData?.parts?.find(candidate => candidate.id === part.id);
            return current !== JSON.stringify(recipePartComparisonSnapshot(original));
        });
        if (changesSharedPart) {
            setSharedWarningOpen(true);
            return;
        }
        await saveForm();
    };

    const addPart = () => {
        setSubmitted(false);
        setForm(previous => ({
            ...previous,
            parts: [...(previous.parts ?? []), makeRecipePart(previous.id)],
            state: initialData ? DTO_STATE.DTO_MODIFIED : previous.state,
        }));
    };
    const attachFromLibrary = (part: IRecipePart) => {
        setForm(previous => ({
            ...previous,
            parts: [...(previous.parts ?? []), {
                ...part,
                recipeId: previous.id,
                isMainPart: false,
                state: DTO_STATE.DTO_UNCHANGED,
                clientOrigin: 'library',
                clientOriginalSnapshot: JSON.stringify(recipePartComparisonSnapshot(part)),
            }],
            state: initialData ? DTO_STATE.DTO_MODIFIED : previous.state,
        }));
        setLibraryOpen(false);
        setLibrarySearch('');
    };
    const openCreateCategory = (partId?: number | string) => {
        setNewCategoryName('');
        setCategoryTargetPartId(partId ?? null);
        setCategoryDialogOpen(true);
    };
    const createCategory = async () => {
        const name = trimOrEmpty(newCategoryName);
        if (!name) return;
        try {
            const category = await createRecipePartCategory({name}).unwrap();
            if (categoryTargetPartId !== null) updatePart(categoryTargetPartId, {categoryId: category.id, categoryName: category.name});
            setCategoryDialogOpen(false);
            setNewCategoryName('');
            setCategoryTargetPartId(null);
        } catch (error) {
            setTraceId(getApiTraceId(error));
            setServerToast(getApiErrorMessage(error, t));
        }
    };
    const saveLibraryPartCategory = async (part: IRecipePart, categoryId: string | null) => {
        setSavingLibraryCategoryId(part.id);
        try {
            const category = partCategories.find(value => value.id === categoryId);
            await updateRecipePart({id: String(part.id), body: recipePartGuidRequest({
                ...part,
                categoryId,
                categoryName: category?.name ?? null,
                state: DTO_STATE.DTO_MODIFIED,
            })}).unwrap();
        } catch (error) {
            setTraceId(getApiTraceId(error));
            setServerToast(getApiErrorMessage(error, t));
        } finally {
            setSavingLibraryCategoryId(null);
        }
    };
    const deletePart = (partId: number | string) => {
        setTouched({});
        setForm(previous => ({
            ...previous,
            parts: (previous.parts ?? []).flatMap(part => {
                if (part.id !== partId) return [part];
                if (part.state === DTO_STATE.DTO_NEW) return [];
                const restoring = part.state === DTO_STATE.DTO_DELETED;
                return [{
                    ...part,
                    state: restoring ? DTO_STATE.DTO_MODIFIED : DTO_STATE.DTO_DELETED,
                    items: restoring
                        ? (part.items ?? []).map(item => ({
                            ...item,
                            state: item.state === DTO_STATE.DTO_DELETED
                                ? DTO_STATE.DTO_MODIFIED
                                : item.state,
                        }))
                        : (part.items ?? []).flatMap(item => item.state === DTO_STATE.DTO_NEW
                            ? []
                            : [{...item, state: DTO_STATE.DTO_DELETED}]),
                }];
            }),
            state: initialData ? DTO_STATE.DTO_MODIFIED : previous.state,
        }));
    };
    const requestPartRemoval = (part: IRecipePart) => {
        if (part.state === DTO_STATE.DTO_NEW || part.clientOrigin === 'library') deletePart(part.id);
        else setPartToDetach(part);
    };
    const deletePartFromLibrary = async () => {
        if (!partToDeleteFromLibrary || libraryDeleteConfirmation !== (partToDeleteFromLibrary.partName ?? '')) return;
        try {
            await deleteRecipePart(String(partToDeleteFromLibrary.id)).unwrap();
            setPartToDeleteFromLibrary(null);
            setLibraryDeleteConfirmation('');
        } catch (error) {
            setPartToDeleteFromLibrary(null);
            setLibraryDeleteConfirmation('');
            setTraceId(getApiTraceId(error));
            setServerToast(getApiErrorMessage(error, t));
        }
    };
    const addItem = (part: IRecipePart) => {
        setSubmitted(false);
        updatePart(part.id, {items: [...(part.items ?? []), makeRecipeItem(part.id)]});
    };
    const deleteItem = (part: IRecipePart, itemId: number | string) => {
        setTouched({});
        setForm(previous => ({
            ...previous,
            parts: previous.parts?.map(entry => entry.id === part.id
                ? {
                    ...entry,
                    items: (entry.items ?? []).flatMap(item => {
                        if (item.id !== itemId) return [item];
                        if (item.state === DTO_STATE.DTO_NEW) return [];
                        return [{
                            ...item,
                            state: item.state === DTO_STATE.DTO_DELETED
                                ? DTO_STATE.DTO_MODIFIED
                                : DTO_STATE.DTO_DELETED,
                        }];
                    }),
                    state: initialData && entry.state !== DTO_STATE.DTO_NEW
                        ? DTO_STATE.DTO_MODIFIED
                        : entry.state,
                }
                : entry),
            state: initialData ? DTO_STATE.DTO_MODIFIED : previous.state,
        }));
    };
    const changed = (current: unknown, original: unknown, invalid = false) => initialData !== undefined && original !== undefined && JSON.stringify(current) !== JSON.stringify(original)
        ? {
            '& .MuiOutlinedInput-root': {
                '& fieldset': {borderColor: invalid ? 'error.main' : 'primary.main', borderWidth: 2},
                '&:hover fieldset': {borderColor: invalid ? 'error.dark' : 'primary.dark', borderWidth: 2},
                '&.Mui-focused fieldset': {borderColor: invalid ? 'error.dark' : 'primary.dark', borderWidth: 2},
            },
        }
        : undefined;

    return (
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{display: 'grid', gap: 2, mt: 2}}>
            <Card variant="outlined">
                <CardContent>
                    <Stack spacing={2}>
                        <Typography variant="h5">{t('recipes:recipe')}</Typography>
                        <TextField required fullWidth sx={changed(form.recipeName, initialData?.recipeName, fieldHasError('recipeName'))} label={t('recipes:recipe_name')} value={form.recipeName ?? ''} onChange={event => { markTouched('recipeName'); setServerErrors(previous => ({...previous, recipeName: ''})); update({recipeName: event.target.value}); }} onBlur={() => markTouched('recipeName')} error={fieldHasError('recipeName')} helperText={fieldError('recipeName') || undefined} FormHelperTextProps={{sx: {overflow: 'hidden', animation: 'recipeNameErrorExpand 180ms ease-out', '@keyframes recipeNameErrorExpand': {from: {maxHeight: 0, opacity: 0, marginTop: 0}, to: {maxHeight: 64, opacity: 1, marginTop: '4px'}}}}}/>
                        <TextField fullWidth multiline minRows={2} sx={changed(form.recipeDescription, initialData?.recipeDescription)} label={t('recipes:description')} value={form.recipeDescription ?? ''} onChange={event => update({recipeDescription: event.target.value})}/>
                        <ProductTypeSelect productTypes={productTypes} value={form.productTypeId ?? null} onChange={productTypeId => update({productTypeId})}/>
                        <Autocomplete
                            multiple
                            options={fillings}
                            value={selectedFillings}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            getOptionLabel={option => option.name}
                            onChange={(_, values) => updateFillings(values)}
                            renderInput={params => <TextField {...params} label={t('recipes:fillings')}/>}
                        />
                    </Stack>
                </CardContent>
            </Card>

            <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between" alignItems={{sm: 'center'}} spacing={1}>
                <Typography variant="h6">{t('recipes:parts')}</Typography>
                <Stack direction={{xs: 'column', sm: 'row'}} spacing={1}>
                    <Button type="button" variant="outlined" onClick={() => setLibraryOpen(true)}>{t('recipes:choose_existing_part')}</Button>
                    <WriteButton type="button" variant="outlined" onClick={addPart}>{t('recipes:create_new_part')}</WriteButton>
                </Stack>
            </Stack>

            {(form.parts ?? []).map((part, partIndex) => {
                const partIsDeleted = part.state === DTO_STATE.DTO_DELETED;
                const visibleItems = (part.items ?? []).filter(item => item.state !== DTO_STATE.DTO_DELETED);
                const selectedIds = new Set(visibleItems.map(item => String(item.itemId)));
                const originalPart = initialData?.parts?.find(original => original.id === part.id);

                return (
                    <Card key={part.id} variant="outlined" sx={partIsDeleted ? {backgroundColor: 'rgba(211, 47, 47, 0.08)', borderColor: 'error.main'} : undefined}>
                        <CardContent>
                            <Stack spacing={2}>
                                {(part.usedByRecipesCount ?? 0) > 1 && <Box><Chip size="small" color="info" label={t('recipes:shared_part_usage', {count: part.usedByRecipesCount})}/><Typography variant="body2" color="text.secondary" sx={{mt: .75}}>{t('recipes:shared_part_edit_hint')}</Typography></Box>}
                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                    <TextField disabled={partIsDeleted} required fullWidth sx={{minWidth: 0, ...changed(part.partName, originalPart?.partName, fieldHasError(`part.${partIndex}.name`))}} label={t('recipes:part_name')} value={part.partName ?? ''} onChange={event => { markTouched(`part.${partIndex}.name`); updatePart(part.id, {partName: event.target.value}); }} onBlur={() => markTouched(`part.${partIndex}.name`)} error={fieldHasError(`part.${partIndex}.name`)} helperText={fieldHasError(`part.${partIndex}.name`) ? errors[`part.${partIndex}.name`] : ' '} FormHelperTextProps={{sx: animatedHelperSx(fieldHasError(`part.${partIndex}.name`))}}/>
                                    <WriteIconButton size="small" sx={{width: 44, height: 44, minWidth: 44, minHeight: 44}} aria-label={t(partIsDeleted ? 'common:restore' : 'recipes:detach_part')} color={partIsDeleted ? 'success' : 'error'} onClick={() => partIsDeleted ? deletePart(part.id) : requestPartRemoval(part)}>{partIsDeleted ? <RestoreIcon/> : <DeleteOutlineIcon/>}</WriteIconButton>
                                </Stack>
                                <Stack direction={{xs: 'column', sm: 'row'}} spacing={1} alignItems={{sm: 'center'}}>
                                    <TextField select fullWidth disabled={partIsDeleted || categoriesLoading} label={t('recipes:part_category')} value={part.categoryId ?? ''} onChange={event => {
                                        const categoryId = event.target.value || null;
                                        const category = partCategories.find(value => value.id === categoryId);
                                        updatePart(part.id, {categoryId, categoryName: category?.name ?? null});
                                    }}>
                                        <MenuItem value="">{t('recipes:uncategorized_parts')}</MenuItem>
                                        {partCategories.map(category => <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>)}
                                    </TextField>
                                    <WriteButton type="button" variant="text" disabled={partIsDeleted} onClick={() => openCreateCategory(part.id)} sx={{whiteSpace: 'nowrap'}}>{t('recipes:create_part_category')}</WriteButton>
                                </Stack>
                                <Divider/>

                                {part.state === DTO_STATE.DTO_NEW && part.clientOrigin !== 'library' && (
                                    <Typography variant="body2" color="text.secondary">{t('recipes:composition_base_hint')}</Typography>
                                )}

                                {(part.items ?? []).map((item, itemIndex) => {
                                    const itemIsDeleted = item.state === DTO_STATE.DTO_DELETED;
                                    const controlsDisabled = partIsDeleted || itemIsDeleted;
                                    const itemNameField = `part.${partIndex}.item.${itemIndex}.name`;
                                    const quantityField = `part.${partIndex}.item.${itemIndex}.quantity`;
                                    const originalItem = originalPart?.items?.find(original => original.id === item.id);

                                    return (
                                        <Box key={item.id} sx={{...recipeItemRowSx, ...(itemIsDeleted ? {p: 1, borderRadius: 1, backgroundColor: 'rgba(211, 47, 47, 0.12)'} : {})}}>
                                            <Autocomplete
                                                disabled={controlsDisabled}
                                                sx={{gridArea: 'name', width: '100%', minWidth: 0, ...changed(item.itemId, originalItem?.itemId, fieldHasError(itemNameField))}}
                                                fullWidth
                                                options={items}
                                                value={items.find(option => option.id === String(item.itemId)) ?? null}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                getOptionLabel={option => option.name ?? ''}
                                                getOptionDisabled={option => selectedIds.has(option.id) && option.id !== String(item.itemId)}
                                                disablePortal
                                                ListboxProps={{sx: {maxHeight: 280, overflowY: 'auto'}}}
                                                onChange={(_, value) => {
                                                    markTouched(itemNameField);
                                                    updateItem(part.id, item.id, {itemId: value ? value.id as unknown as number : -1, itemName: value?.name ?? '', unitOfWeight: value?.unitOfWeight ?? ''});
                                                }}
                                                renderInput={params => (
                                                    <TextField {...params} required label={t('items:item_name_recipes')} onBlur={() => markTouched(itemNameField)} error={fieldHasError(itemNameField)} helperText={fieldHasError(itemNameField) ? errors[itemNameField] : ' '} FormHelperTextProps={{sx: animatedHelperSx(fieldHasError(itemNameField))}}/>
                                                )}
                                            />
                                            <LocalizedDecimalField
                                                disabled={controlsDisabled}
                                                sx={{gridArea: 'quantity', width: '100%', minWidth: 0, ...changed(itemQuantity(item), originalItem ? itemQuantity(originalItem) : undefined, fieldHasError(quantityField))}}
                                                fullWidth
                                                label={t('recipes:ingredient_quantity')}
                                                value={itemQuantity(item)}
                                                inputProps={{min: 0, step: 'any'}}
                                                InputProps={{endAdornment: selectedIngredientUnit(item.itemId, item.unitOfWeight) ? <InputAdornment position="end">{selectedIngredientUnit(item.itemId, item.unitOfWeight)}</InputAdornment> : undefined}}
                                                onValueChange={quantity => {
                                                    markTouched(quantityField);
                                                    updateItem(part.id, item.id, {proportions: updatePrimaryProportionQuantity(item, quantity as number)});
                                                }}
                                                onBlur={() => markTouched(quantityField)}
                                                error={fieldHasError(quantityField)}
                                                helperText={fieldHasError(quantityField) ? errors[quantityField] : ' '}
                                                FormHelperTextProps={{sx: animatedHelperSx(fieldHasError(quantityField))}}
                                            />
                                            <WriteIconButton size="small" sx={{...recipeItemActionSx, mt: 0.75}} disabled={partIsDeleted} aria-label={t(itemIsDeleted ? 'common:restore' : 'common:delete')} color={itemIsDeleted ? 'success' : 'error'} onClick={() => deleteItem(part, item.id)}>{itemIsDeleted ? <RestoreIcon/> : <DeleteOutlineIcon/>}</WriteIconButton>
                                        </Box>
                                    );
                                })}

                                <WriteButton disabled={partIsDeleted} type="button" variant="text" onClick={() => addItem(part)}>{t('recipes:add_new_item')}</WriteButton>
                            </Stack>
                        </CardContent>
                    </Card>
                );
            })}

            <Typography color="error" sx={{minHeight: 24}}>{hasVisibleValidationErrors && hasValidationErrors ? t('recipes:validation_fix_errors') : ' '}</Typography>
            {recipeNamesError && <Alert severity="error">{getApiErrorMessage(recipeNamesError, t)}</Alert>}
            {submitError && <Alert severity="error">{submitError}</Alert>}
            <Box><WriteButton type="submit" variant="contained" disabled={saving || namesUnavailable || hasValidationErrors || (!!initialData && !isDirty)} endIcon={<SendIcon/>}>{submitLabel}</WriteButton></Box>
            <Snackbar open={!!serverToast} autoHideDuration={6000} onClose={() => setServerToast('')}><Alert severity="error" onClose={() => setServerToast('')}>{serverToast}<TraceIdFeedback traceId={traceId}/></Alert></Snackbar>
            <Dialog open={libraryOpen} onClose={() => setLibraryOpen(false)} fullWidth maxWidth="sm" PaperProps={{sx: {m: {xs: 1, sm: 4}, width: {xs: 'calc(100% - 16px)', sm: '100%'}, maxHeight: {xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)'}}}}>
                <DialogTitle sx={{px: {xs: 2, sm: 3}, py: {xs: 1.5, sm: 2}}}>{t('recipes:choose_existing_part')}</DialogTitle>
                <DialogContent sx={{px: {xs: 1.5, sm: 3}, pb: {xs: 1, sm: 2}}}>
                    <Box sx={{display: 'grid', gridTemplateColumns: {xs: 'minmax(0, 1fr) minmax(0, 1fr)', sm: 'minmax(0, 1fr) minmax(0, 1fr)'}, gap: 1, mt: 0.5}}>
                        <TextField fullWidth label={t('recipes:search_parts')} value={librarySearch} onChange={event => setLibrarySearch(event.target.value)}/>
                        <TextField select fullWidth label={t('recipes:part_category')} value={libraryCategory} onChange={event => setLibraryCategory(event.target.value)}>
                            <MenuItem value="all">{t('recipes:all_part_categories')}</MenuItem>
                            <MenuItem value="uncategorized">{t('recipes:uncategorized_parts')}</MenuItem>
                            {partCategories.map(category => <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>)}
                        </TextField>
                    </Box>
                    <WriteButton type="button" size="small" onClick={() => openCreateCategory()} sx={{mt: 0.5, px: 0.5}}>{t('recipes:create_part_category')}</WriteButton>
                    <Stack spacing={1} sx={{mt: 1}}>
                        {libraryLoading && <Typography>{t('recipes:loading_parts')}</Typography>}
                        {libraryParts.map(part => {
                            const alreadyAttached = (form.parts ?? []).some(current => current.id === part.id && current.state !== DTO_STATE.DTO_DELETED);
                            return <Card key={part.id} variant="outlined"><CardContent sx={{p: {xs: 1.5, sm: 2}, '&:last-child': {pb: {xs: 1.5, sm: 2}}}}>
                                <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between" spacing={{xs: 0.75, sm: 1.5}}>
                                    <Box sx={{minWidth: 0, flex: 1}}>
                                        <Typography fontWeight={600}>{part.partName}</Typography>
                                        <Chip
                                            size="small"
                                            color={(part.usedByRecipesCount ?? 0) === 0 ? 'warning' : 'info'}
                                            label={(part.usedByRecipesCount ?? 0) === 0
                                                ? t('recipes:part_not_used')
                                                : t('recipes:part_usage', {count: part.usedByRecipesCount})}
                                            sx={{mt: 0.5, height: 24, maxWidth: '100%'}}
                                        />
                                        <TextField select size="small" fullWidth label={t('recipes:part_category')} value={part.categoryId ?? ''} disabled={isReadOnly || savingLibraryCategoryId === part.id} onChange={event => void saveLibraryPartCategory(part, event.target.value || null)} sx={{mt: 0.75, maxWidth: {sm: 260}}}>
                                            <MenuItem value="">{t('recipes:uncategorized_parts')}</MenuItem>
                                            {partCategories.map(category => <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>)}
                                        </TextField>
                                        <Stack spacing={0.25} sx={{mt: 0.75}}>
                                            {(part.items ?? []).map(item => {
                                                const quantity = getIngredientAmount(item, 1).quantity;
                                                const unit = item.unitOfWeight === UNIT_OF_WEIGHT.G
                                                    ? t('items:grams_short')
                                                    : item.unitOfWeight === UNIT_OF_WEIGHT.PCS
                                                        ? t('items:pcs')
                                                        : item.unitOfWeight;
                                                return <Stack key={item.id} direction="row" justifyContent="space-between" spacing={2}>
                                                    <Typography variant="body2" noWrap title={item.itemName}>{item.itemName}</Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'nowrap'}}>{formatDecimal(quantity)} {unit}</Typography>
                                                </Stack>;
                                            })}
                                        </Stack>
                                    </Box>
                                    <Stack direction="row" spacing={1} alignItems="center"
                                           justifyContent={{xs: !alreadyAttached && (part.usedByRecipesCount ?? 0) === 0 ? 'space-between' : 'flex-end', sm: 'flex-end'}}
                                           sx={{width: {xs: '100%', sm: 'auto'}, flexShrink: 0, alignSelf: {sm: 'center'}}}>
                                        {!alreadyAttached && (part.usedByRecipesCount ?? 0) === 0 && <WriteButton size="small" color="error" sx={{fontSize: '0.72rem', lineHeight: 1.2, px: 0.5, minWidth: 0}} onClick={() => { setLibraryDeleteConfirmation(''); setPartToDeleteFromLibrary(part); }}>{t('recipes:delete_part_from_library')}</WriteButton>}
                                        <WriteButton size="small" disabled={alreadyAttached} onClick={() => attachFromLibrary(part)} sx={{ml: 'auto'}}>{alreadyAttached ? t('recipes:part_already_attached') : t('recipes:attach_part')}</WriteButton>
                                    </Stack>
                                </Stack>
                            </CardContent></Card>;
                        })}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{px: {xs: 1.5, sm: 3}, py: {xs: 1, sm: 2}}}><Button onClick={() => setLibraryOpen(false)}>{t('common:cancel')}</Button></DialogActions>
            </Dialog>
            <Dialog open={sharedWarningOpen} onClose={() => setSharedWarningOpen(false)}>
                <DialogTitle>{t('recipes:edit_shared_part_title')}</DialogTitle>
                <DialogContent><Typography>{t('recipes:edit_shared_part_warning')}</Typography></DialogContent>
                <DialogActions><Button onClick={() => setSharedWarningOpen(false)}>{t('common:cancel')}</Button><WriteButton variant="contained" onClick={() => { setSharedWarningOpen(false); void saveForm(); }}>{t('recipes:edit_shared_part_confirm')}</WriteButton></DialogActions>
            </Dialog>
            <Dialog open={!!partToDetach} onClose={() => setPartToDetach(null)}>
                <DialogTitle>{t('recipes:detach_part_title', {name: partToDetach?.partName})}</DialogTitle>
                <DialogContent><Typography>{t('recipes:detach_part_warning')}</Typography></DialogContent>
                <DialogActions><Button onClick={() => setPartToDetach(null)}>{t('common:cancel')}</Button><WriteButton color="error" variant="contained" onClick={() => { if (partToDetach) deletePart(partToDetach.id); setPartToDetach(null); }}>{t('recipes:detach_part')}</WriteButton></DialogActions>
            </Dialog>
            <Dialog open={!!partToDeleteFromLibrary} onClose={() => { if (!deletingLibraryPart) { setPartToDeleteFromLibrary(null); setLibraryDeleteConfirmation(''); } }}>
                <DialogTitle>{t('recipes:delete_library_part_title', {name: partToDeleteFromLibrary?.partName})}</DialogTitle>
                <DialogContent>
                    <Typography>{t('recipes:delete_library_part_warning')}</Typography>
                    <Typography sx={{mt: 2}}>{t('recipes:delete_library_part_confirmation')}</Typography>
                    <TextField fullWidth margin="dense" value={libraryDeleteConfirmation} onChange={event => setLibraryDeleteConfirmation(event.target.value)} label={partToDeleteFromLibrary?.partName ?? ''}/>
                </DialogContent>
                <DialogActions><Button disabled={deletingLibraryPart} onClick={() => { setPartToDeleteFromLibrary(null); setLibraryDeleteConfirmation(''); }}>{t('common:cancel')}</Button><WriteButton disabled={deletingLibraryPart || libraryDeleteConfirmation !== (partToDeleteFromLibrary?.partName ?? '')} color="error" variant="contained" onClick={() => void deletePartFromLibrary()}>{t('recipes:delete_part_from_library')}</WriteButton></DialogActions>
            </Dialog>
            <Dialog open={categoryDialogOpen} onClose={() => { if (!creatingCategory) { setCategoryDialogOpen(false); setNewCategoryName(''); setCategoryTargetPartId(null); } }} fullWidth maxWidth="xs">
                <DialogTitle>{t('recipes:create_part_category')}</DialogTitle>
                <DialogContent><TextField autoFocus fullWidth margin="dense" label={t('recipes:part_category_name')} value={newCategoryName} onChange={event => setNewCategoryName(event.target.value)}/></DialogContent>
                <DialogActions><Button disabled={creatingCategory} onClick={() => { setCategoryDialogOpen(false); setNewCategoryName(''); setCategoryTargetPartId(null); }}>{t('common:cancel')}</Button><WriteButton disabled={creatingCategory || !trimOrEmpty(newCategoryName)} variant="contained" onClick={() => void createCategory()}>{t('common:create')}</WriteButton></DialogActions>
            </Dialog>
        </Box>
    );
}
